import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { ArrowRight, BookOpen, Mail, Feather, CalendarDays, PenLine, HeartHandshake, BookMarked, UserRound, Crown } from "lucide-react";
import { toast } from "sonner";

function HomeSignup() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/newsletter/signup", { email, source: "homepage_hero" });
      toast.success(data.already_subscribed ? "You're already on the list." : "You're in. Watch your inbox.");
      setEmail("");
      setDone(true);
    } catch {
      toast.error("Could not sign up. Please try again.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <section className="relative overflow-hidden" style={{ background: "var(--bg-alt)" }}>
      <div className="absolute -top-20 -right-20 w-[28rem] h-[28rem] rounded-full opacity-[0.12]" style={{ background: "radial-gradient(circle, var(--ember), transparent 70%)" }} />
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-12 md:py-24 lg:py-32 relative grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-7">
          <div className="overline mb-4">The monthly letter — free</div>
          <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl tracking-[-0.04em] leading-[0.95]" style={{ color: "var(--ink)" }}>
            One letter a month.<br />
            <span className="font-serif-display-italic" style={{ color: "var(--ember)" }}>Worth opening.</span>
          </h2>
          <p className="mt-6 max-w-xl text-base md:text-lg leading-relaxed" style={{ color: "var(--ink-2)" }}>
            Long-form essays, scripture, reading notes, and behind-the-scenes from the writing desk —
            from Cody &amp; Samantha. No spam, no funnels. Unsubscribe with one click.
          </p>
        </div>
        <div className="lg:col-span-5">
          <form onSubmit={onSubmit} data-testid="home-newsletter-form" className="card-surface p-6 lg:p-8">
            <label className="overline mb-3 block" htmlFor="home-email">Your email</label>
            <div className="flex gap-2">
              <input
                id="home-email"
                type="email"
                required
                placeholder="your@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="home-newsletter-email"
                className="flex-1 px-4 py-3 text-base outline-none"
                style={{ background: "var(--bg-base)", border: "1px solid var(--border-strong)", color: "var(--ink)" }}
              />
              <button type="submit" disabled={busy} data-testid="home-newsletter-submit" className="btn-primary disabled:opacity-50">
                {busy ? "…" : "Sign up"} <ArrowRight size={14} />
              </button>
            </div>
            {done && (
              <p className="mt-4 text-sm" style={{ color: "var(--gold)" }}>
                Thank you — keep an eye on your inbox.
              </p>
            )}
            <p className="mt-4 text-xs" style={{ color: "var(--muted)" }}>
              By signing up you agree to receive occasional emails from The Disciple Life.
              We will never share your address.
            </p>
          </form>
        </div>
      </div>
    </section>
  );
}


const HUB = [
  { to: "/blog", label: "Journal", body: "Honest essays and articles on the Christian life.", icon: PenLine },
  { to: "/books", label: "Books", body: "Books and Bible studies by Cody D. Thrash.", icon: BookMarked },
  { to: "/read-with-me", label: "Read With Me", body: "A daily Bible reading with reflection.", icon: CalendarDays },
  { to: "/counseling", label: "Counseling", body: "Pre-marriage, marriage mentoring and individual counseling.", icon: HeartHandshake },
  { to: "/about", label: "About", body: "Meet Cody & Samantha and the heart of the ministry.", icon: UserRound },
  { to: "/membership", label: "Members", body: "Verse-by-verse notes, series, and the full archive.", icon: Crown },
];

function MinistryHub() {
  return (
    <section className="max-w-7xl mx-auto px-6 lg:px-12 pt-14 md:pt-20 pb-6" data-testid="ministry-hub">
      <div className="overline mb-3" style={{ color: "var(--gold)" }}>The Disciple Life · Ministry</div>
      <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl tracking-[-0.04em] leading-[0.95]" style={{ color: "var(--ink)" }}>
        A ministry of writing, Bible study, <span style={{ color: "var(--gold)" }}>and Discipleship.</span>
      </h1>
      <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {HUB.map(({ to, label, body, icon: Icon }) => (
          <Link key={to} to={to} className="group card-surface p-6 flex items-start gap-4 transition-transform duration-200 hover:-translate-y-0.5" data-testid={`ministry-hub-${label.toLowerCase().replace(/\s+/g, "-")}`}>
            <span className="shrink-0 w-10 h-10 rounded-full flex items-center justify-center" style={{ background: "var(--surface-hover)", color: "var(--gold)" }}><Icon size={18} /></span>
            <span className="min-w-0">
              <span className="flex items-center gap-2 font-serif-display text-xl" style={{ color: "var(--ink)" }}>{label} <ArrowRight size={14} className="opacity-0 group-hover:opacity-100 transition-opacity" style={{ color: "var(--gold)" }} /></span>
              <span className="block text-sm mt-1" style={{ color: "var(--ink-2)" }}>{body}</span>
            </span>
          </Link>
        ))}
      </div>
    </section>
  );
}

export default function Ministry() {
  const [books, setBooks] = useState([]);
  const [todayReading, setTodayReading] = useState(null);
  useEffect(() => {
    api.get("/books").then(({ data }) => setBooks(data.filter((b) => b.featured).slice(0, 2)));
    api.get("/read-with-me/today").then(({ data }) => setTodayReading(data || null)).catch(() => setTodayReading(null));
  }, []);

  const todayLabel = new Date().toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" });

  return (
    <div data-testid="ministry-page">
      <MinistryHub />
      {/* TODAY'S READ WITH ME — public, top of home */}
      {todayReading && (
        <section data-testid="home-devotional" className="relative" style={{ background: "var(--bg-alt)", borderBottom: "1px solid var(--border-subtle)" }}>
          <div className="max-w-5xl mx-auto px-6 lg:px-12 py-10 md:py-16 lg:py-24">
            <div className="flex items-center gap-3 flex-wrap mb-6">
              <CalendarDays size={16} strokeWidth={2} style={{ color: "var(--ember)" }} />
              <span className="overline" style={{ fontSize: "0.65rem", color: "var(--gold)" }}>{todayLabel}</span>
              <span className="px-2 py-0.5 text-[0.6rem] uppercase tracking-[0.18em]" style={{ background: "var(--ember)", color: "var(--bg-base)" }}>
                Today · Read With Me
              </span>
              <span className="overline" style={{ fontSize: "0.6rem", color: "var(--muted)" }}>
                Day {todayReading.day_number}
              </span>
            </div>
            {todayReading.title && (
              <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl tracking-[-0.03em] leading-[1]" style={{ color: "var(--ink)" }}>
                {todayReading.title}
              </h2>
            )}
            <div className="mt-5 space-y-1">
              <div className="overline" style={{ fontSize: "0.6rem" }}>Today's reading</div>
              <p className="font-serif-display-italic text-2xl lg:text-3xl" style={{ color: "var(--ember)" }}>
                {todayReading.passage}
              </p>
              {todayReading.key_scripture && (
                <p className="font-serif-display-italic text-lg lg:text-xl mt-2" style={{ color: "var(--terracotta)" }}>
                  {todayReading.key_scripture}
                </p>
              )}
            </div>
            <div className="editorial-rule my-8" />
            {todayReading.reflection && (
              <div
                className="text-base lg:text-lg leading-[1.85] whitespace-pre-line max-w-3xl"
                style={{ color: "var(--ink-2)" }}
                data-testid="home-devo-body"
              >
                {todayReading.reflection}
              </div>
            )}
            <div className="mt-10 p-6 lg:p-8 border" style={{ borderColor: "var(--border-subtle)", background: "var(--bg-base)" }}>
              <div className="overline mb-2" style={{ color: "var(--gold)" }}>For members</div>
              <p className="text-sm lg:text-base" style={{ color: "var(--ink-2)" }}>
                Members also see Cody's verse-by-verse notes, the full archive of past days, and a daily reading calendar.
              </p>
              <div className="mt-5 flex flex-wrap items-center gap-4">
                <Link to="/membership" data-testid="home-devo-membership-cta" className="btn-primary">
                  Become a member <ArrowRight size={14} />
                </Link>
                <Link to="/read-with-me" data-testid="home-devo-archive-cta" className="text-xs uppercase tracking-[0.2em]" style={{ color: "var(--ember)" }}>
                  Members → open Read With Me
                </Link>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* HERO */}
      <section
        className="relative overflow-hidden surface-deep"
        style={{
          backgroundImage: `linear-gradient(115deg, rgba(14,34,64,0.92) 0%, rgba(14,34,64,0.72) 45%, rgba(14,34,64,0.35) 100%), url('https://customer-assets.emergentagent.com/job_writer-shop-3/artifacts/ikjm7ent_tdl%20background%201.png')`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="absolute top-12 right-12 hidden lg:block font-serif-display text-[14rem] leading-none opacity-[0.07] select-none" style={{ color: "var(--gold)" }}>
          01
        </div>

        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-16 md:py-32 lg:py-44 relative">
          <div className="overline mb-8" style={{ color: "var(--gold)" }}>
            Equip · Disciple · Grow
          </div>
          <h2 className="font-serif-display leading-[0.9] tracking-[-0.045em]" style={{ color: "#FFFFFF" }}>
            <span className="block text-5xl sm:text-6xl lg:text-8xl">EQUIPPING BELIEVERS.</span>
            <span className="block text-5xl sm:text-6xl lg:text-8xl mt-2">
              BUILDING <span style={{ color: "var(--gold)" }}>DISCIPLES.</span>
            </span>
            <span className="block font-serif-display-italic text-4xl sm:text-5xl lg:text-7xl mt-6 pl-1" style={{ color: "var(--gold)" }}>
              Changing lives.
            </span>
          </h2>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-12 gap-8 items-end">
            <p className="md:col-span-7 text-base md:text-lg leading-relaxed" style={{ color: "#E5DBC4" }}>
              A ministry of writing run by a husband and wife. Books, Bible studies, and honest essays
              for disciples of Jesus who are tired of shallow Christianity and hungry for something true.
            </p>
            <div className="md:col-span-5 flex flex-wrap gap-4 md:justify-end">
              <Link to="/books" data-testid="hero-shop-button" className="btn-primary">
                Read the books <ArrowRight size={16} />
              </Link>
              <Link to="/membership" data-testid="hero-membership-button" className="btn-ghost">
                Join the members
              </Link>
            </div>
          </div>

          <div className="mt-16 flex items-center gap-4 text-xs uppercase tracking-[0.3em]" style={{ color: "var(--gold)" }}>
            <span className="w-12 h-px" style={{ background: "var(--gold)" }} />
            <span>Matthew 11:28 — Come to me</span>
          </div>
        </div>
      </section>

      {/* NEWSLETTER LEAD CAPTURE */}
      <HomeSignup />

      {/* THREE PILLARS */}
      <section className="max-w-7xl mx-auto px-6 lg:px-12 py-12 md:py-24 lg:py-32">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
          {[
            { icon: BookOpen, title: "Books to keep on the nightstand", body: "Devotionals and discipleship resources written for ordinary mornings and ordinary saints." },
            { icon: Feather, title: "A monthly letter, not a feed", body: "Long-form reflections, reading notes, and behind-the-scenes from the writing desk." },
            { icon: Mail, title: "A library for members", body: "Weekly Bible studies, early chapters, and quiet community for those walking the road with us." },
          ].map((p, i) => (
            <div key={i} className="border-t pt-6" style={{ borderColor: "var(--border-strong)" }}>
              <p.icon size={28} strokeWidth={1.5} style={{ color: "var(--terracotta)" }} />
              <h3 className="font-serif-display text-2xl mt-5 leading-tight" style={{ color: "var(--ink)" }}>{p.title}</h3>
              <p className="mt-3 text-base leading-relaxed" style={{ color: "var(--ink-2)" }}>{p.body}</p>
            </div>
          ))}
        </div>
      </section>

      {false && (<>
      {/* FEATURED BOOKS */}
      <section style={{ background: "var(--bg-alt)" }}>
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-12 md:py-24">
          <div className="flex items-end justify-between mb-12">
            <div>
              <div className="overline mb-3">From the shelf</div>
              <h2 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl tracking-tight" style={{ color: "var(--ink)" }}>Featured books</h2>
            </div>
            <Link to="/books" data-testid="home-all-books-link" className="hidden md:inline-flex items-center gap-2 text-sm" style={{ color: "var(--terracotta)" }}>
              See all books <ArrowRight size={14} />
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
            {books.map((b) => (
              <Link key={b.id} to={`/books/${b.id}`} data-testid={`featured-book-${b.id}`} className="group block">
                <div className="aspect-[2/3] overflow-hidden mb-5 flex items-center justify-center" style={{ background: "var(--surface)" }}>
                  <img src={b.cover_url} alt={b.title} className="w-full h-full object-contain group-hover:scale-[1.02] transition-transform duration-500" />
                </div>
                <div className="overline mb-2">{b.category}</div>
                <h3 className="font-serif-display text-2xl lg:text-3xl leading-tight" style={{ color: "var(--ink)" }}>{b.title}</h3>
                {b.subtitle && <p className="mt-1 italic" style={{ color: "var(--ink-2)" }}>{b.subtitle}</p>}
                <p className="mt-4 text-sm" style={{ color: "var(--terracotta)" }}>${b.price.toFixed(2)} — read more <ArrowRight size={14} className="inline" /></p>
              </Link>
            ))}
          </div>
        </div>
      </section>
      </>)}

      {/* QUOTE */}
      <section className="max-w-5xl mx-auto px-6 lg:px-12 py-16 md:py-32 text-center">
        <p className="font-serif-display text-3xl md:text-5xl leading-snug italic" style={{ color: "var(--ink)" }}>
          “Come to me, all you who are weary and burdened, and I will give you rest.”
        </p>
        <div className="overline mt-8">Matthew 11:28</div>
      </section>

      {/* MEMBER CTA */}
      <section className="max-w-5xl mx-auto px-6 lg:px-12 py-12 md:py-24 text-center">
        <div className="overline mb-4">Members-only</div>
        <h2 className="font-serif-display text-4xl md:text-6xl tracking-[-0.04em] leading-[1]" style={{ color: "var(--ink)" }}>
          Walk the narrow road, <br />
          <span className="font-serif-display-italic" style={{ color: "var(--ember)" }}>but not alone.</span>
        </h2>
        <p className="mt-8 max-w-2xl mx-auto text-base md:text-lg leading-relaxed" style={{ color: "var(--ink-2)" }}>
          For $4.99 a month, members receive Read With Me (Cody's daily walk through Scripture with verse notes and full reflections), the weekly Bible study library, the monthly
          newsletter, and early access to new chapters before they go to print.
        </p>
        <div className="mt-10 flex flex-wrap gap-4 justify-center">
          <Link to="/membership" data-testid="member-cta-button" className="btn-primary">Become a member</Link>
          <Link to="/about" data-testid="about-link-cta" className="btn-ghost">Meet Cody &amp; Samantha</Link>
        </div>
      </section>
    </div>
  );
}
